import type { FormikConfig } from 'formik'
import { Formik } from 'formik'
import React from 'react'

import { useDashboardFeatureFlags } from '@components'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  HeadContent,
  Loader,
  SaveShortcutListener,
  Subsection,
} from '@fourthwall/components'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'

import type { FormValues } from './General.types'
import { Analytics } from './components/Analytics/Analytics'
import { CookieSettings } from './components/CookieSettings/CookieSettings'
import { CreatorDetails } from './components/CreatorDetails/CreatorDetails'
import { SiteDetails } from './components/SiteDetails/SiteDetails'
import { Timezone } from './components/Timezone/Timezone'

type GeneralViewProps = {
  initialValues: FormValues
  validationSchema: FormikConfig<FormValues>['validationSchema']
  isLoading: boolean
  isSubmitting: boolean
  onMarketingReportDownload(): void
  onSubmit: (values: FormValues) => void
}

export const GeneralView = ({
  initialValues,
  validationSchema,
  isLoading,
  isSubmitting,
  onMarketingReportDownload,
  onSubmit,
}: GeneralViewProps) => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Loader size="large" />
      </div>
    )
  }

  return (
    <>
      <HeadContent title="General &ndash; Settings" />
      <Formik<FormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ dirty, submitForm, resetForm, isValid }) => (
          <>
            <CreatorDetails />
            <SiteDetails />

            <Subsection title="Email marketing">
              <ButtonClean
                label="Download subscribers list (CSV)"
                onClick={onMarketingReportDownload}
              />
            </Subsection>

            {dashboardFeatureFlags.analyticsTrackingPixels.enabled && <Analytics />}

            <CookieSettings />

            {dashboardFeatureFlags.timezone.enabled && <Timezone />}

            <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
            <ConfirmBar
              isOpen={dirty}
              isLoading={isSubmitting}
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </>
        )}
      </Formik>
    </>
  )
}
